import React, { useEffect, useState } from 'react';
import { SdfGrid, SdfShimmer, SdfAlert, SdfTooltip } from '@synerg/react-components';
import axios from 'axios';
import get from 'lodash/get';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment-timezone';
import { getFormattedStringDate } from '../common';
// import 'ag-grid-community/styles/ag-grid.css';
// import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './partnerOrgDetails.scss';
import Toast from '../common/toast';
import { uniqueId } from 'lodash';

export default function CertificateDetails({ partner }) {
  const [certificates, setCertificates] = useState([]);
  const [isBusy, setIsBusy] = useState(false);
  const [toast, setToast] = useState(false);

  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true
    });
  };

  const getCertificates = async () => {
    setIsBusy(true);
    try {
      const ooid = partner.ooid;
      const { data: { certificates = [] } } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${ooid}/certificates`);
      setCertificates(certificates);
      setIsBusy(false);
    } catch (e) {
      setIsBusy(false);
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching certificates. Please try again.');
      handleResponse(errorMessage);
    }
  };

  const certName = (data) => {
    return (
      <SdfTooltip trigger="hover" state="neutral" tooltipId={uniqueId()} attachmentPoint={'right-end'} portalEnabled={'true'}>
        <div slot='tooltip-target' style={{ color: "#393533" }} className='overflow-ellipsis overflow-hidden font-default'>{data}</div>
        <div className='font-default'>{data}</div>
      </SdfTooltip>
    )
  };

  const certDate = (data) => {
    return (
      <div style={{ color: "#393533" }} className='leading-sm mb-3 flex items-center font-default'>{data}</div>
    )
  };

  function renderCertMessage({ data }) {
    const cert = data;
    const expiresIn = moment.tz(cert.expires, 'America/New_York').diff(moment.tz(new Date(), 'America/New_York'), 'days');
    const expiryInfo = { message: 'Certificate expiry date unavailable', alertType: 'warning' };

    if (cert.status && cert.status === 'failed') {
      expiryInfo.message = `Generation Failed. Please try request again or contact your technical advisor.`;
      expiryInfo.alertType = 'error'
    } else if (cert.status && cert.status === 'processing') {
      expiryInfo.message = `Generating...`
      expiryInfo.alertType = 'info'
    } else if (expiresIn > 0) {
      expiryInfo.message = `${expiresIn} days left before certificate expires`;
      if (expiresIn > 90) {
        expiryInfo.alertType = 'neutral'
      }
      if (expiresIn <= 90 && expiresIn > 60) {
        expiryInfo.alertType = 'info'
      }
      if (expiresIn <= 60 && expiresIn >= 1) {
        expiryInfo.alertType = 'warning'
      }

    } else if (expiresIn <= 0) {
      expiryInfo.alertType = 'error';
      expiryInfo.message = 'Certificate expired';
    }

    return (
      <SdfAlert style={{ wordBreak: "break-word" }} type='inline' status={expiryInfo.alertType}>
        <div className="text-xs">{expiryInfo.message}</div>
      </SdfAlert>
    )
  };

  useEffect(() => {
    getCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = [
    {
      field: "commonName",
      headerName: "Certificate name",
      headerClass: "text-heading-01",
      flex: 4,
      suppressMovable: true,
      cellRenderer: params => certName(params.data.commonName)
    },
    {
      field: "createdAt",
      headerName: "Upload date",
      headerClass: "text-heading-01",
      flex: 3,
      suppressMovable: true,
      autoHeight: true,
      wrapText: true,
      cellRenderer: params => certDate(getFormattedStringDate(params.data.createdAt))
    },
    {
      field: "expires",
      headerName: "Expiration date",
      headerClass: "text-heading-01",
      flex: 3,
      suppressMovable: true,
      autoHeight: true,
      wrapText: true,
      cellRenderer: params => certDate(getFormattedStringDate(params.data.expires))
    },
    {
      field: "status",
      headerName: "Status message",
      headerClass: "text-heading-01",
      flex: 5,
      wrapText: true,
      cellRenderer: event => renderCertMessage(event),
      suppressMovable: true
    }
  ];

  return (
    <div>
      {toast && <Toast
        key={toast.key}
        status={toast.status}
        message={toast.message}
        showToast={toast.showToast}
      />}
      <div className="text-heading-05 font-bold mt-8">{`Certificates (${certificates.length})`}</div>
      <div className="text-body-02 mt-2">Track certficates’ expiration status.</div>
      {isBusy ? <SdfShimmer rows={3} /> :
        <div className="mt-6 certificate-tab">
          <SdfGrid className="ag-theme-alpine">
            <AgGridReact
              overlayNoRowsTemplate={"No certificates found."}
              rowHeight={54}
              headerHeight={54}
              domLayout='autoHeight'
              rowData={certificates}
              rowClass="pt-1 cursor-pointer"
              autoSizeAllColumns={true}
              columnDefs={columnDefs}
            />
          </SdfGrid>
        </div>
      }
    </div>
  )
}
